import { z } from 'zod'

import {
  EventExternalSourceProviderEnum,
  EventInviteeSchema,
  EventModuleLinkSchema,
} from './event'

// Calendar event type enum
export const CalendarEventTypeEnum = z.enum([
  'task',
  'event',
  'google_calendar',
  'outlook_calendar',
])
export type CalendarEventType = z.infer<typeof CalendarEventTypeEnum>

// Calendar view types
export const CalendarViewEnum = z.enum(['month', 'week', 'day', 'agenda'])
export type CalendarView = z.infer<typeof CalendarViewEnum>

// Base calendar event schema
export const CalendarEventBaseSchema = z.object({
  id: z.string(),
  type: CalendarEventTypeEnum,
  title: z.string(),
  start: z.coerce.date(),
  end: z.coerce.date().optional(),
  allDay: z.boolean(),
  color: z.string().optional(),
})

export type CalendarEventBase = z.infer<typeof CalendarEventBaseSchema>

// Calendar assignee
export const CalendarAssigneeSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
})

export type CalendarAssignee = z.infer<typeof CalendarAssigneeSchema>

// Calendar task (task displayed on calendar)
export const CalendarTaskSchema = CalendarEventBaseSchema.extend({
  type: z.literal('task'),
  taskDateField: z.enum(['due', 'scheduled']),
  taskNumber: z.string(),
  status: z.string(),
  statusTypeId: z.string().optional(),
  priority: z.string(),
  projectId: z.string().optional(),
  projectName: z.string().optional(),
  entityName: z.string().optional(),
  assignees: z.array(CalendarAssigneeSchema),
  isRecurring: z.boolean(),
  isVirtualRecurrence: z.boolean().optional(),
  templateTaskId: z.string().optional(),
  moduleType: z.string().optional(),
  moduleId: z.string().optional(),
})

export type CalendarTask = z.infer<typeof CalendarTaskSchema>

export interface TaskCalendarDatePair {
  dueDate: Date
  scheduledFor: Date
  dueDateHasTime?: boolean | null
  isAllDay?: boolean | null
}

/**
 * Returns true when a task's due and scheduled values represent the same
 * visible calendar occurrence. Timed values are compared at the minute
 * precision used by the calendar UI; all-day values are compared by date.
 */
export function areTaskCalendarDatesSameOccurrence({
  dueDate,
  scheduledFor,
  dueDateHasTime,
  isAllDay,
}: TaskCalendarDatePair): boolean {
  const dueHasTime = dueDateHasTime ?? false
  const scheduledHasTime = !(isAllDay ?? true)

  if (dueHasTime !== scheduledHasTime) return false

  if (dueHasTime) {
    return (
      Math.floor(dueDate.getTime() / 60_000) ===
      Math.floor(scheduledFor.getTime() / 60_000)
    )
  }

  const dateKey = (date: Date) => date.toISOString().slice(0, 10)
  return dateKey(dueDate) === dateKey(scheduledFor)
}

// Calendar attendee (for external events)
export const CalendarAttendeeSchema = z.object({
  email: z.string(),
  name: z.string().optional(),
  responseStatus: z
    .enum(['accepted', 'declined', 'tentative', 'needsAction'])
    .optional(),
})

export type CalendarAttendee = z.infer<typeof CalendarAttendeeSchema>

export const CalendarAttendeeSuggestionSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  source: z.string(),
  detail: z.string().optional(),
})

export type CalendarAttendeeSuggestion = z.infer<
  typeof CalendarAttendeeSuggestionSchema
>

// External calendar event (Google/Outlook)
export const CalendarExternalEventSchema = CalendarEventBaseSchema.extend({
  type: z.enum(['google_calendar', 'outlook_calendar']),
  externalId: z.string(),
  location: z.string().optional(),
  description: z.string().optional(),
  timezone: z.string().optional(),
  attendees: z.array(CalendarAttendeeSchema).optional(),
  calendarId: z.string(),
  calendarName: z.string(),
  // Which integration connection (account) the event came from — routes
  // edits/deletes back to the right account when several are connected
  connectionId: z.string().optional(),
  htmlLink: z.string().optional(),
  // Join URL for a video conference (Google Meet/Zoom/Teams), when present.
  // Used by the desktop meeting-record reminder to fire only for video calls.
  conferenceUrl: z.string().optional(),
  conferenceProvider: z.string().optional(),
  isBusy: z.boolean(),
  provider: EventExternalSourceProviderEnum.optional(),
  iCalUid: z.string().optional(),
  recurringEventId: z.string().optional(),
  originalStartTime: z.string().optional(),
  etag: z.string().optional(),
  updatedAt: z.coerce.date().optional(),
  organizerEmail: z.string().optional(),
  status: z.string().optional(),
  canEdit: z.boolean().optional(),
})

export type CalendarExternalEvent = z.infer<typeof CalendarExternalEventSchema>

export const CalendarMeetingEventSchema = CalendarEventBaseSchema.extend({
  type: z.literal('event'),
  eventId: z.string().uuid(),
  description: z.string().optional(),
  location: z.string().optional(),
  timezone: z.string().optional(),
  attendees: z.array(EventInviteeSchema).default([]),
  htmlLink: z.string().optional(),
  conferenceUrl: z.string().optional(),
  conferenceProvider: z.string().optional(),
  isBusy: z.boolean().default(true),
  status: z.string().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().nullable().optional(),
  isExternallyManaged: z.boolean().default(false),
  externalSourceProvider: EventExternalSourceProviderEnum.optional(),
  externalCalendarId: z.string().optional(),
  externalEventId: z.string().optional(),
  recurringEventId: z.string().optional(),
  originalStartTime: z.string().optional(),
  canEdit: z.boolean().optional(),
  moduleLinks: z.array(EventModuleLinkSchema).optional(),
})

export type CalendarMeetingEvent = z.infer<typeof CalendarMeetingEventSchema>

// Union type for any calendar event
export const CalendarEventSchema = z.union([
  CalendarTaskSchema,
  CalendarMeetingEventSchema,
  CalendarExternalEventSchema,
])

export type CalendarEvent = z.infer<typeof CalendarEventSchema>

// Calendar query parameters
export const CalendarQueryParamsSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  includeTasks: z.boolean().optional().default(true),
  includeScheduledTasks: z.boolean().optional().default(false),
  includeExternalEvents: z.boolean().optional().default(true),
  projectIds: z.array(z.string()).optional(),
  assigneeIds: z.array(z.string()).optional(),
  priorities: z.array(z.string()).optional(),
  statuses: z.array(z.string()).optional(),
  tagIds: z.array(z.string()).optional(),
  view: CalendarViewEnum.optional(),
})

export type CalendarQueryParams = z.infer<typeof CalendarQueryParamsSchema>

export const CalendarEventAttendeeInputSchema = z.object({
  email: z.string().email(),
  name: z.string().optional(),
})

export type CalendarEventAttendeeInput = z.infer<
  typeof CalendarEventAttendeeInputSchema
>

export const CalendarEventWriteInputSchema = z.object({
  calendarId: z.string().optional(),
  connectionId: z.string().optional(),
  title: z.string().min(1),
  description: z.string().optional(),
  location: z.string().optional(),
  start: z.string(),
  end: z.string().optional(),
  timezone: z.string().optional(),
  allDay: z.boolean().optional(),
  attendees: z.array(CalendarEventAttendeeInputSchema).optional(),
  recurrence: z.array(z.string()).optional(),
  createMeetLink: z.boolean().optional(),
  sendUpdates: z.enum(['all', 'externalOnly', 'none']).optional(),
})

export type CalendarEventWriteInput = z.infer<
  typeof CalendarEventWriteInputSchema
>

export const CalendarEventUpdateInputSchema =
  CalendarEventWriteInputSchema.extend({
    calendarId: z.string(),
    eventId: z.string(),
  })

export type CalendarEventUpdateInput = z.infer<
  typeof CalendarEventUpdateInputSchema
>

export const CalendarEventDeleteInputSchema = z.object({
  calendarId: z.string(),
  eventId: z.string(),
  connectionId: z.string().optional(),
  sendUpdates: z.enum(['all', 'externalOnly', 'none']).optional(),
})

export type CalendarEventDeleteInput = z.infer<
  typeof CalendarEventDeleteInputSchema
>

export const CalendarSuggestedTimeSchema = z.object({
  start: z.string(),
  end: z.string(),
})

export type CalendarSuggestedTime = z.infer<typeof CalendarSuggestedTimeSchema>

export const CalendarSuggestTimesInputSchema = z.object({
  calendarId: z.string().optional(),
  connectionId: z.string().optional(),
  start: z.string(),
  end: z.string(),
  timezone: z.string().optional(),
  attendees: z.array(CalendarEventAttendeeInputSchema).optional(),
})

export type CalendarSuggestTimesInput = z.infer<
  typeof CalendarSuggestTimesInputSchema
>

// Sync status for external calendars
export const CalendarSyncStatusSchema = z.object({
  lastSyncAt: z.coerce.date().optional(),
  status: z.enum(['idle', 'syncing', 'success', 'error']),
  errorMessage: z.string().optional(),
})

export type CalendarSyncStatus = z.infer<typeof CalendarSyncStatusSchema>

// Calendar events response metadata
export const CalendarMetaSchema = z.object({
  dateRange: z.object({
    start: z.string(),
    end: z.string(),
  }),
  taskCount: z.number(),
  eventCount: z.number().default(0),
  externalEventCount: z.number(),
  syncStatus: z
    .object({
      googleCalendar: CalendarSyncStatusSchema.optional(),
      outlookCalendar: CalendarSyncStatusSchema.optional(),
    })
    .optional(),
})

export type CalendarMeta = z.infer<typeof CalendarMetaSchema>

// Calendar events API response
export const CalendarEventsResponseSchema = z.object({
  tasks: z.array(CalendarTaskSchema),
  events: z.array(CalendarMeetingEventSchema).default([]),
  externalEvents: z.array(CalendarExternalEventSchema),
  meta: CalendarMetaSchema,
})

export type CalendarEventsResponse = z.infer<
  typeof CalendarEventsResponseSchema
>

// Calendar filter state (for frontend store)
export interface CalendarFilters {
  projectIds: string[]
  assigneeIds: string[]
  priorities: string[]
  statuses: string[]
  tagIds: string[]
}

// Calendar preferences (persisted)
export interface CalendarPreferences {
  showExternalEvents: boolean
  showTasks: boolean
  defaultView: CalendarView
}

// Calendar state (for frontend store)
export interface CalendarState {
  currentView: CalendarView
  currentDate: Date
  showExternalEvents: boolean
  showTasks: boolean
  filters: CalendarFilters
}

// Helper function to check if event is a task
export function isCalendarTask(event: CalendarEvent): event is CalendarTask {
  return event.type === 'task'
}

export function isCalendarMeetingEvent(
  event: CalendarEvent,
): event is CalendarMeetingEvent {
  return event.type === 'event'
}

// Helper function to check if event is external
export function isCalendarExternalEvent(
  event: CalendarEvent,
): event is CalendarExternalEvent {
  return event.type === 'google_calendar' || event.type === 'outlook_calendar'
}

// Default calendar filters
export const DEFAULT_CALENDAR_FILTERS: CalendarFilters = {
  projectIds: [],
  assigneeIds: [],
  priorities: [],
  statuses: [],
  tagIds: [],
}

// Default calendar preferences
export const DEFAULT_CALENDAR_PREFERENCES: CalendarPreferences = {
  showExternalEvents: true,
  showTasks: true,
  defaultView: 'month',
}
